import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Capacitor } from '@capacitor/core';
import {
  DeviceInfo,
  ClipItem,
  ConnectionStatus,
  UserSettings
} from './types';
import { SyncEngine } from './syncEngine';
import { Navbar } from './components/Navbar';
import { LandingPage } from './components/LandingPage';
import { Footer } from './components/Footer';
import { CookieBanner } from './components/CookieBanner';
import { PwaInstallBanner } from './components/PwaInstallBanner';
import { RoomHeader } from './components/RoomHeader';
import { DevicePresence } from './components/DevicePresence';
import { ClipboardHistory } from './components/ClipboardHistory';
import { QuickDrop } from './components/QuickDrop';
import { SettingsModal } from './components/SettingsModal';
import { QrModal } from './components/QrModal';
import { JoinRoomModal } from './components/JoinRoomModal';
import { ClipModal } from './components/ClipModal';
import { ShortcutsModal } from './components/ShortcutsModal';
import { ParticleBackground } from './components/ParticleBackground';
import { QrScannerModal } from './components/QrScannerModal';
import { FlowDeskModal } from './components/FlowDeskModal';
import { FlowLandingPage } from './components/FlowLandingPage';
import { DownloadHubModal } from './components/DownloadHubModal';
import { SetupGuideModal } from './components/SetupGuideModal';
import { LegalModals, LegalDocType } from './components/LegalModals';
import { UpdateModal } from './components/UpdateModal';
import { checkRemoteVersion, VersionInfo } from './utils/updateChecker';
import { 
  AlertCircle, 
  CheckCircle2, 
  ShieldAlert, 
  ShieldCheck, 
  Keyboard,
  UploadCloud,
  ArrowLeft
} from 'lucide-react';

const DEFAULT_SETTINGS: UserSettings = {
  autoSyncToClipboard: true,
  soundEnabled: true,
  pauseSync: false,
  relayUrl: '',
  enableWebRTC: true
};

export const App: React.FC = () => {
  const isNative = Capacitor.isNativePlatform();

  const isFlowDomain = typeof window !== 'undefined' && (
    window.location.hostname.includes('ck-flow') ||
    window.location.hostname.includes('ckflow') ||
    window.location.search.includes('flow') ||
    window.location.pathname.startsWith('/flow')
  );

  const [roomId, setRoomId] = useState<string>(() => localStorage.getItem('ck_room_id') || '');
  const [accessCode, setAccessCode] = useState<string>(() => localStorage.getItem('ck_access_code') || '');
  const [status, setStatus] = useState<ConnectionStatus>('disconnected');
  const [peers, setPeers] = useState<DeviceInfo[]>([]);
  const [clips, setClips] = useState<ClipItem[]>(() => {
    try {
      const saved = localStorage.getItem('ck_clips_history');
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });

  const [settings, setSettings] = useState<UserSettings>(() => {
    try {
      const saved = localStorage.getItem('ck_user_settings');
      return saved ? { ...DEFAULT_SETTINGS, ...JSON.parse(saved) } : DEFAULT_SETTINGS;
    } catch {
      return DEFAULT_SETTINGS;
    }
  });

  // Current View on Web: 'landing' or 'console' or 'flow'
  const [currentView, setCurrentView] = useState<'landing' | 'console' | 'flow'>(() => {
    if (isNative) return 'console';
    if (isFlowDomain) return 'flow';
    return (localStorage.getItem('ck_room_id') && localStorage.getItem('ck_access_code')) ? 'console' : 'landing';
  });


  const [isJoinModalOpen, setIsJoinModalOpen] = useState(false);
  const [isQrOpen, setIsQrOpen] = useState(false);
  const [isFlowDeskOpen, setIsFlowDeskOpen] = useState(false);
  const [isScannerOpen, setIsScannerOpen] = useState(false);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [isShortcutsOpen, setIsShortcutsOpen] = useState(false);
  const [isDownloadsOpen, setIsDownloadsOpen] = useState(false);
  const [isGuideOpen, setIsGuideOpen] = useState(false);
  const [guideTab, setGuideTab] = useState<'android' | 'windows' | 'security'>('android');
  const [isLegalOpen, setIsLegalOpen] = useState(false);
  const [legalDoc, setLegalDoc] = useState<LegalDocType>('privacy');
  const [updateInfo, setUpdateInfo] = useState<VersionInfo | null>(null);
  const [isUpdateModalOpen, setIsUpdateModalOpen] = useState(false);

  const [toastMessage, setToastMessage] = useState<{ text: string; type: 'info' | 'success' | 'error' } | null>(null);
  const [inspectingClip, setInspectingClip] = useState<ClipItem | null>(null);
  const [isWindowDragging, setIsWindowDragging] = useState(false);

  const syncEngineRef = useRef<SyncEngine | null>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const quickDropTextareaRef = useRef<HTMLTextAreaElement>(null);
  const dragCounterRef = useRef(0);

  const showToast = useCallback((text: string, type: 'info' | 'success' | 'error' = 'info') => {
    setToastMessage({ text, type });
    setTimeout(() => setToastMessage(null), 3000);
  }, []);

  const openGuide = (tab: 'android' | 'windows' | 'security' = 'android') => {
    setGuideTab(tab);
    setIsGuideOpen(true);
  };

  const openLegal = (doc: LegalDocType) => {
    setLegalDoc(doc);
    setIsLegalOpen(true);
  };

  // Check URL parameters for direct invite link on boot
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const roomParam = params.get('room');
    const keyParam = params.get('key');
    if (roomParam && keyParam) {
      handleJoinRoom(roomParam, keyParam);
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);

  // Initialize Sync Engine
  useEffect(() => {
    syncEngineRef.current = new SyncEngine(settings, {
      onStatusChange: (newStatus) => setStatus(newStatus),
      onPeersChange: (newPeers) => setPeers(newPeers),
      onClipReceived: (clip) => {
        setClips((prev) => {
          const filtered = prev.filter((c) => c.id !== clip.id);
          const updated = [clip, ...filtered].slice(0, 100);
          try {
            localStorage.setItem('ck_clips_history', JSON.stringify(updated));
          } catch {}
          return updated;
        });
      },
      onRateLimited: (msg) => showToast(msg, 'error'),
      onError: (err) => showToast(err, 'error')
    });

    if (roomId && accessCode) {
      syncEngineRef.current.connect(roomId, accessCode);
    }

    return () => {
      syncEngineRef.current?.disconnect();
    };
  }, []);

  // Global Keyboard Shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) {
        if (e.key === 'Escape') {
          (e.target as HTMLElement).blur();
        }
        return;
      }

      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setIsShortcutsOpen((prev) => !prev);
      }

      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'f') {
        e.preventDefault();
        searchInputRef.current?.focus();
      }

      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'n') {
        e.preventDefault();
        quickDropTextareaRef.current?.focus();
      }

      if (e.key === 'Escape') {
        setIsQrOpen(false);
        setIsSettingsOpen(false);
        setIsShortcutsOpen(false);
        setIsScannerOpen(false);
        setIsFlowDeskOpen(false);
        setIsDownloadsOpen(false);
        setIsGuideOpen(false);
        setIsLegalOpen(false);
        setIsJoinModalOpen(false);
        setIsUpdateModalOpen(false);
        setInspectingClip(null);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Drag and Drop broadcast
  useEffect(() => {
    const handleDragEnter = (e: DragEvent) => {
      e.preventDefault();
      dragCounterRef.current += 1;
      if (e.dataTransfer?.types.includes('Files')) {
        setIsWindowDragging(true);
      }
    };

    const handleDragLeave = (e: DragEvent) => {
      e.preventDefault();
      dragCounterRef.current -= 1;
      if (dragCounterRef.current === 0) {
        setIsWindowDragging(false);
      }
    };

    const handleDragOver = (e: DragEvent) => {
      e.preventDefault();
    };

    const handleDrop = async (e: DragEvent) => {
      e.preventDefault();
      dragCounterRef.current = 0;
      setIsWindowDragging(false);

      if (status !== 'connected') {
        showToast('Connect to a room to broadcast dropped files', 'error');
        return;
      }

      if (e.dataTransfer?.files && e.dataTransfer.files.length > 0) {
        const file = e.dataTransfer.files[0];
        try {
          const reader = new FileReader();
          reader.onload = async () => {
            const dataUrl = reader.result as string;
            await syncEngineRef.current?.pushClipboard(dataUrl, true, file.name, file.type);
            showToast(`Broadcasting file "${file.name}"`, 'success');
          };
          reader.readAsDataURL(file);
        } catch (err: any) {
          showToast(err.message || 'File drop failed', 'error');
        }
      }
    };

    window.addEventListener('dragenter', handleDragEnter);
    window.addEventListener('dragleave', handleDragLeave);
    window.addEventListener('dragover', handleDragOver);
    window.addEventListener('drop', handleDrop);

    return () => {
      window.removeEventListener('dragenter', handleDragEnter);
      window.removeEventListener('dragleave', handleDragLeave);
      window.removeEventListener('dragover', handleDragOver);
      window.removeEventListener('drop', handleDrop);
    };
  }, [status, showToast]);

  const handleJoinRoom = async (newRoomId: string, newAccessCode: string) => {
    setRoomId(newRoomId);
    setAccessCode(newAccessCode);
    localStorage.setItem('ck_room_id', newRoomId);
    localStorage.setItem('ck_access_code', newAccessCode);

    setIsJoinModalOpen(false);
    setCurrentView('console');

    if (syncEngineRef.current) {
      await syncEngineRef.current.connect(newRoomId, newAccessCode);
      showToast(`Connected to room: ${newRoomId}`, 'success');
    }
  };

  const handleDisconnect = () => {
    syncEngineRef.current?.disconnect();
    setRoomId('');
    setAccessCode('');
    localStorage.removeItem('ck_room_id');
    localStorage.removeItem('ck_access_code');
    setStatus('disconnected');
    setPeers([]);
    showToast('Disconnected from mesh room', 'info');
  };

  const handlePushText = async (text: string) => {
    if (!syncEngineRef.current || status !== 'connected') {
      showToast('Cannot broadcast text: Not connected to room', 'error');
      return;
    }
    try {
      await syncEngineRef.current.pushClipboard(text, true);
      showToast('Text broadcasted to mesh', 'success');
    } catch (err: any) {
      showToast(err.message || 'Failed to send text', 'error');
    }
  };

  const handlePushFile = async (fileDataUrl: string, fileName: string, fileMimeType: string) => {
    if (!syncEngineRef.current || status !== 'connected') {
      showToast('Cannot broadcast file: Not connected to room', 'error');
      return;
    }
    try {
      await syncEngineRef.current.pushClipboard(fileDataUrl, true, fileName, fileMimeType);
      showToast(`File "${fileName}" sent to mesh`, 'success');
    } catch (err: any) {
      showToast(err.message || 'Failed to send file', 'error');
    }
  };

  const handleTogglePin = (id: string) => {
    setClips((prev) => {
      const updated = prev.map((c) => (c.id === id ? { ...c, isPinned: !c.isPinned } : c));
      try {
        localStorage.setItem('ck_clips_history', JSON.stringify(updated));
      } catch {}
      return updated;
    });
  };

  const handleDeleteClip = (id: string) => {
    setClips((prev) => {
      const updated = prev.filter((c) => c.id !== id);
      try {
        localStorage.setItem('ck_clips_history', JSON.stringify(updated));
      } catch {}
      return updated;
    });
    showToast('Clip removed from local device', 'info');
  };

  const handleClearAll = () => {
    setClips((prev) => {
      const pinnedOnly = prev.filter((c) => c.isPinned);
      try {
        localStorage.setItem('ck_clips_history', JSON.stringify(pinnedOnly));
      } catch {}
      return pinnedOnly;
    });
    showToast('Cleared all unpinned history', 'info');
  };

  const handleSaveSettings = (newSettings: UserSettings) => {
    setSettings(newSettings);
    localStorage.setItem('ck_user_settings', JSON.stringify(newSettings));
    syncEngineRef.current?.updateSettings(newSettings);
    showToast('Settings saved successfully', 'success');
  };

  const handleUpdateDeviceName = (name: string) => {
    syncEngineRef.current?.updateDeviceName(name);
    showToast(`Device name changed to "${name}"`, 'success');
  };

  const handleLaunchWebApp = () => {
    if (roomId && accessCode) {
      setCurrentView('console');
    } else {
      setIsJoinModalOpen(true);
    }
  };

  const currentDevice: DeviceInfo = {
    deviceId: syncEngineRef.current ? (syncEngineRef.current as any).device?.deviceId || 'local-web' : 'local-web',
    deviceName: syncEngineRef.current ? (syncEngineRef.current as any).device?.deviceName || 'This Device' : 'This Device',
    os: 'web',
    clientType: 'web',
    joinedAt: Date.now(),
    lastPing: Date.now()
  };

  return (
    <div className="min-h-screen bg-[#05070E] text-slate-100 flex flex-col font-sans relative selection:bg-cyan-500 selection:text-black overflow-x-hidden">
      
      {/* Background Animated Particles */}
      <ParticleBackground />

      {/* WEB NAVBAR (Only displayed on Web Browser when on Landing Page or when no active room) */}
      {!isNative && currentView === 'landing' && (
        <Navbar
          onOpenDownloads={() => setIsDownloadsOpen(true)}
          onOpenGuide={openGuide}
          onLaunchWebApp={handleLaunchWebApp}
          hasActiveRoom={Boolean(roomId && accessCode)}
          onViewLiveConsole={() => setCurrentView('console')}
          onNavigateHome={() => setCurrentView('landing')}
          onOpenFlow={() => setCurrentView('flow')}
        />
      )}

      {/* Global Drag Overlay */}
      {isWindowDragging && (
        <div className="fixed inset-0 z-50 bg-black/90 backdrop-blur-2xl border-4 border-dashed border-cyan-400 flex flex-col items-center justify-center gap-4 animate-in fade-in duration-150">
          <div className="w-20 h-20 rounded-3xl bg-cyan-950/60 border border-cyan-500/50 flex items-center justify-center text-cyan-400 shadow-glow-cyan animate-bounce">
            <UploadCloud size={40} />
          </div>
          <h2 className="text-2xl font-bold text-white font-display">Drop File to Broadcast Instantly</h2>
          <p className="text-sm text-cyan-300">Will be encrypted with 256-bit AES-GCM and sent to all connected mesh nodes</p>
        </div>
      )}

      {/* Floating Toast Notification */}
      {toastMessage && (
        <div
          className={`fixed top-5 right-5 z-50 flex items-center gap-3 px-4 sm:px-5 py-3 rounded-2xl border text-xs sm:text-sm font-bold shadow-2xl backdrop-blur-2xl animate-in slide-in-from-top-4 ${
            toastMessage.type === 'error'
              ? 'bg-rose-950/90 text-rose-200 border-rose-800/80 shadow-rose-950/50'
              : toastMessage.type === 'success'
              ? 'bg-emerald-950/90 text-emerald-200 border-emerald-800/80 shadow-emerald-950/50'
              : 'bg-slate-900/90 text-cyan-200 border-cyan-800/80 shadow-cyan-950/50'
          }`}
        >
          {toastMessage.type === 'error' ? (
            <ShieldAlert size={18} className="text-rose-400" />
          ) : toastMessage.type === 'success' ? (
            <CheckCircle2 size={18} className="text-emerald-400" />
          ) : (
            <AlertCircle size={18} className="text-cyan-400" />
          )}
          <span>{toastMessage.text}</span>
        </div>
      )}

      {/* MAIN VIEW CONTROLLER */}
      {!isNative && currentView === 'flow' ? (
        /* 1. DEDICATED CK FLOW MULTI-PC LANDING PAGE */
        <main className="flex-1">
          <FlowLandingPage
            onOpenDownloads={() => setIsDownloadsOpen(true)}
            onOpenGuide={openGuide}
            onSwitchToShare={() => setCurrentView('landing')}
          />
        </main>
      ) : !isNative && currentView === 'landing' ? (
        /* 2. MARKETING WEBSITE LANDING PAGE (Web Browser Only) */
        <main className="flex-1">
          <LandingPage
            onOpenDownloads={() => setIsDownloadsOpen(true)}
            onOpenGuide={openGuide}
            onLaunchWebApp={handleLaunchWebApp}
            onOpenScanner={() => setIsScannerOpen(true)}
            hasActiveRoom={Boolean(roomId && accessCode)}
            onViewLiveConsole={() => setCurrentView('console')}
          />
        </main>
      ) : (
        /* 3. LIVE APPLICATION WORKSPACE (Native App or Active Web Session) */
        <main className="flex-1 w-full max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-4 sm:py-6 flex flex-col justify-between">
          <div className="space-y-4">
            
            {/* Top Switcher: Return to Website (Only on Web) */}
            {!isNative && (
              <div className="flex items-center justify-between pb-3 border-b border-white/10 text-xs">
                <button
                  onClick={() => setCurrentView('landing')}
                  className="flex items-center gap-1.5 text-cyan-400 hover:text-cyan-300 font-semibold transition-colors bg-cyan-500/10 hover:bg-cyan-500/20 border border-cyan-500/30 px-3 py-1.5 rounded-xl cursor-pointer"
                >
                  <ArrowLeft size={14} />
                  <span>← Back to Website &amp; Features</span>
                </button>

                <div className="flex items-center gap-2 text-slate-400 font-mono text-[11px]">
                  <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
                  <span>Live App Active</span>
                </div>
              </div>
            )}

            <RoomHeader
              roomId={roomId}
              accessCode={accessCode}
              status={status}
              peerCount={peers.length}
              onOpenQr={() => setIsQrOpen(true)}
              onOpenFlow={() => setIsFlowDeskOpen(true)}
              onOpenSettings={() => setIsSettingsOpen(true)}
              onOpenDownloads={() => setIsDownloadsOpen(true)}
              onOpenGuide={openGuide}
              onDisconnect={handleDisconnect}
              onNewRoom={handleDisconnect}
            />

            {roomId && accessCode ? (
              /* ACTIVE ROOM MASTER-DETAIL WORKSPACE */
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-5 items-start">
                
                {/* Left Master Pane: Mesh Radar & Telemetry */}
                <div className="lg:col-span-4 space-y-4">
                  <DevicePresence 
                    currentDevice={currentDevice} 
                    peers={peers}
                    onRenameDevice={handleUpdateDeviceName}
                    onOpenQr={() => setIsQrOpen(true)}
                  />

                  {/* Cryptography & Architecture Status Card */}
                  <div className="native-glass rounded-2xl p-4 border border-white/10 shadow-xl space-y-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <ShieldCheck size={16} className="text-cyan-400" />
                        <span className="text-xs font-bold text-slate-200 uppercase tracking-wider font-display">
                          Security &amp; Mesh
                        </span>
                      </div>
                      <span className="text-[10px] font-mono font-bold text-emerald-400 bg-emerald-950/40 border border-emerald-500/30 px-2 py-0.5 rounded-full">
                        E2EE Active
                      </span>
                    </div>

                    <div className="space-y-2 text-xs">
                      <div className="flex items-center justify-between p-2 rounded-xl bg-[#080A14] border border-white/5">
                        <span className="text-slate-400">Cipher Engine</span>
                        <span className="font-mono text-cyan-300 font-bold">AES-GCM-256</span>
                      </div>
                      <div className="flex items-center justify-between p-2 rounded-xl bg-[#080A14] border border-white/5">
                        <span className="text-slate-400">Key Derivation</span>
                        <span className="font-mono text-slate-300">PBKDF2 (100k)</span>
                      </div>
                      <div className="flex items-center justify-between p-2 rounded-xl bg-[#080A14] border border-white/5">
                        <span className="text-slate-400">Server Storage</span>
                        <span className="font-mono text-emerald-400 font-bold">0 B (Ephemeral)</span>
                      </div>
                    </div>

                    <div className="pt-2 border-t border-white/5 flex items-center justify-between text-[11px] text-slate-400">
                      <button
                        onClick={() => setIsShortcutsOpen(true)}
                        className="hover:text-cyan-300 flex items-center gap-1.5 transition-colors cursor-pointer"
                      >
                        <Keyboard size={13} />
                        <span>Hotkeys (Ctrl+K)</span>
                      </button>
                      <button
                        onClick={() => openGuide('security')}
                        className="hover:text-cyan-300 transition-colors cursor-pointer"
                      >
                        Whitepaper →
                      </button>
                    </div>
                  </div>
                </div>

                {/* Right Detail Pane: Composer & Clipboard Stream */}
                <div className="lg:col-span-8 space-y-4">
                  <QuickDrop
                    onPushText={handlePushText}
                    onPushFile={handlePushFile}
                    disabled={status !== 'connected'}
                    textareaRef={quickDropTextareaRef}
                  />

                  <ClipboardHistory
                    clips={clips}
                    onCopyClip={(c) => showToast(`Copied ${c.metadata.type} to OS clipboard`, 'success')}
                    onTogglePin={handleTogglePin}
                    onDeleteClip={handleDeleteClip}
                    onClearAll={handleClearAll}
                    onInspectClip={(c) => setInspectingClip(c)}
                    searchRef={searchInputRef}
                  />
                </div>
              </div>
            ) : (
              /* NO ACTIVE ROOM: JOIN / CREATE ROOM VIEW */
              <div className="py-6 sm:py-10 my-auto flex items-center justify-center">
                <JoinRoomModal 
                  onJoin={handleJoinRoom} 
                  onOpenScanner={() => setIsScannerOpen(true)} 
                  onOpenDownloads={() => setIsDownloadsOpen(true)}
                  onOpenGuide={openGuide}
                />
              </div>
            )}
          </div>

          {/* Minimalist Native App Footer */}
          <footer className="mt-8 pt-4 pb-2 border-t border-white/5 flex flex-col sm:flex-row items-center justify-between gap-2 text-[11px] text-slate-500 font-mono">
            <div className="flex items-center gap-2">
              <span className="w-1.5 h-1.5 rounded-full bg-cyan-400" />
              <span>CK Share Desktop &amp; Mobile Mesh</span>
              <span>•</span>
              <span>256-bit AES-GCM E2EE</span>
            </div>

            <div className="flex items-center gap-4">
              <button onClick={() => openGuide('security')} className="hover:text-slate-300 transition-colors cursor-pointer">
                Security
              </button>
              <button onClick={() => openLegal('privacy')} className="hover:text-slate-300 transition-colors cursor-pointer">
                Privacy
              </button>
              <button onClick={() => setIsShortcutsOpen(true)} className="hover:text-slate-300 transition-colors cursor-pointer">
                Shortcuts
              </button>
            </div>
          </footer>
        </main>
      )}

      {/* Join/Create Room Modal Overlay (when launched from Landing Page on Web) */}
      {isJoinModalOpen && (
        <div 
          onClick={(e) => {
            if (e.target === e.currentTarget) setIsJoinModalOpen(false);
          }}
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/85 backdrop-blur-xl animate-fadeIn"
        >
          <div className="relative w-full max-w-xl">
            <JoinRoomModal 
              onClose={() => setIsJoinModalOpen(false)}
              onJoin={handleJoinRoom} 
              onOpenScanner={() => {
                setIsJoinModalOpen(false);
                setIsScannerOpen(true);
              }}
              onOpenDownloads={() => {
                setIsJoinModalOpen(false);
                setIsDownloadsOpen(true);
              }}
              onOpenGuide={(tab) => {
                setIsJoinModalOpen(false);
                openGuide(tab);
              }}
            />
          </div>
        </div>
      )}

      {/* OVERLAY MODALS */}
      <QrModal
        isOpen={isQrOpen}
        onClose={() => setIsQrOpen(false)}
        roomId={roomId}
        accessCode={accessCode}
      />

      <QrScannerModal
        isOpen={isScannerOpen}
        onClose={() => setIsScannerOpen(false)}
        onScanSuccess={handleJoinRoom}
      />

      <SettingsModal
        isOpen={isSettingsOpen}
        onClose={() => setIsSettingsOpen(false)}
        settings={settings}
        device={currentDevice}
        onSaveSettings={handleSaveSettings}
        onUpdateDeviceName={handleUpdateDeviceName}
        onOpenUpdateModal={(info) => {
          setUpdateInfo(info);
          setIsUpdateModalOpen(true);
        }}
      />

      <UpdateModal
        isOpen={isUpdateModalOpen}
        onClose={() => setIsUpdateModalOpen(false)}
        versionInfo={updateInfo}
      />

      <ClipModal
        clip={inspectingClip}
        onClose={() => setInspectingClip(null)}
        onCopy={(c) => showToast(`Copied ${c.metadata.type} to clipboard`, 'success')}
      />

      <ShortcutsModal
        isOpen={isShortcutsOpen}
        onClose={() => setIsShortcutsOpen(false)}
      />

      <FlowDeskModal
        isOpen={isFlowDeskOpen}
        onClose={() => setIsFlowDeskOpen(false)}
        peers={peers}
        currentDevice={currentDevice}
        onOpenDownloads={() => {
          setIsFlowDeskOpen(false);
          setIsDownloadsOpen(true);
        }}
      />

      <DownloadHubModal
        isOpen={isDownloadsOpen}
        onClose={() => setIsDownloadsOpen(false)}
        onOpenGuide={openGuide}
      />

      <SetupGuideModal
        isOpen={isGuideOpen}
        onClose={() => setIsGuideOpen(false)}
        initialTab={guideTab}
        onOpenDownloads={() => {
          setIsGuideOpen(false);
          setIsDownloadsOpen(true);
        }}
      />

      <LegalModals
        isOpen={isLegalOpen}
        onClose={() => setIsLegalOpen(false)}
        initialDoc={legalDoc}
      />

      {/* Web Only Overlays & Footer */}
      {!isNative && currentView === 'landing' && (
        <>
          <CookieBanner onOpenPrivacy={() => openLegal('privacy')} />
          <PwaInstallBanner />
          <Footer
            onOpenDownloads={() => setIsDownloadsOpen(true)}
            onOpenGuide={openGuide}
            onOpenLegal={openLegal}
          />
        </>
      )}

    </div>
  );
};

export default App;
